import React, { useState, useMemo } from "react";
import { format, parseISO, isValid } from "date-fns";
import { zhTW } from "date-fns/locale";
import { DayPicker, SelectSingleEventHandler } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { Adapt, Button, Popover, Sheet, XStack, YStack } from "tamagui";
import { colors } from "@styles/colors";

type DataProps = {
  id: string;
  placeholder?: string;
};

type DateItemProps = {
  data: DataProps;
  hasError?: boolean;
  value?: string | Date | null;
  onChange: (date: string | null) => void;
};

const DateItem = ({ data, hasError, onChange, value }: DateItemProps) => {
  const [open, setOpen] = useState(false);
  const themeColor = colors.primary;

  //-- 解析傳入的日期
  const selectedDate = useMemo(() => {
    if (!value) return undefined;
    const date = typeof value === "string" ? parseISO(value) : value;
    return isValid(date) ? date : undefined;
  }, [value]);

  const displayDate = selectedDate ? format(selectedDate, "yyyy-MM-dd") : "";
  const handleSelect: SelectSingleEventHandler = (date) => {
    if (date) {
      onChange(format(date, "yyyy-MM-dd"));
      setOpen(false);
    } else {
      onChange(null);
    }
  };

  return (
    <Popover keepChildrenMounted open={open} onOpenChange={setOpen} size="$5" allowFlip placement="bottom-start">
      <Popover.Trigger asChild>
        <Button
          borderColor={hasError ? "$red10" : open ? "$placeholderColor" : "$borderColor"}
          borderWidth={open ? 2 : 1}
          backgroundColor="#f8f8f8"
          justifyContent="flex-start"
          onPress={() => setOpen(true)}
          color={displayDate ? "$color" : "$placeholderColor"}
          width="100%">
          {displayDate || data?.placeholder || "請選擇日期"}
        </Button>
      </Popover.Trigger>

      <Adapt when="sm" platform="touch">
        <Sheet animation="medium" modal dismissOnSnapToBottom={false} dismissOnOverlayPress={false} snapPoints={[65]}>
          <Sheet.Frame padding="$4">
            <Sheet.ScrollView>
              <Adapt.Contents />
            </Sheet.ScrollView>
          </Sheet.Frame>
          <Sheet.Overlay animation="lazy" enterStyle={{ opacity: 0 }} exitStyle={{ opacity: 0 }} />
        </Sheet>
      </Adapt>

      <Popover.Content borderWidth={1} enterStyle={{ y: -10, opacity: 0 }} exitStyle={{ y: -10, opacity: 0 }} elevate padding={0} animation="quick">
        <Popover.Arrow borderWidth={1} borderColor="$borderColor" />

        <YStack padding="$3" alignItems="center">
          <DayPicker
            {...({
              mode: "single",
              selected: selectedDate,
              onSelect: handleSelect,
            } as any)}
            locale={zhTW}
            captionLayout="dropdown"
            fromYear={1900}
            toYear={new Date().getFullYear()}
            toDate={new Date()}
            disabled={{ after: new Date() }}
            style={{
              // @ts-ignore
              "--rdp-accent-color": themeColor,
              "--rdp-background-alpha": "0.1",
              "--rdp-today-color": themeColor,
            }}
            footer={
              <XStack justifyContent="flex-end" marginTop="$2" width="100%">
                <Button
                  size="$2"
                  chromeless
                  onPress={() => {
                    onChange(null);
                    setOpen(false);
                  }}
                  color={themeColor}>
                  清除
                </Button>
              </XStack>
            }
          />
        </YStack>
      </Popover.Content>
    </Popover>
  );
};

export default DateItem;
